import { useAtom, useAtomValue } from 'jotai';
import { useTheme, Menu, MenuItem, Button } from '@mui/material';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../layout/PageHeader';
import { ContentWrapper } from '../layout/styled';
import { AccontantTimesheetStatusesTable } from './components/AccontantTimesheetStatusesTable';
import { isSmallScreenState } from '../../state/UIState';
import { EXPORT_MODE } from '../../types';
import { Typography } from '../../components/Typography/Typography';
import { useToastMessage } from '../../hooks/useToastMessage';
import { ReactComponent as DownLoadIcon } from '../../assets/icons/download-icon.svg';
import { ExportMultiTimesheetModal } from './components/ExportMultiTimesheetModal/ExportMultiTimesheetModal';
import { ApprovalDetailsModal } from '../TimesheetApprovals/components/ApprovalDetailsModal';
import { approvalDetailsModalState } from '../../state/UIAccountantState';
import { useOpenTimesheetsToExport } from '../../queries/hooks/useOpenTimesheetsToExport';
import { generateActivityLogsTemplate } from '../../utils/excelMultiExportTimesheet';

export function AccountantTimesheetStatuses() {
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const { colors } = useTheme();
  const { pushWarningToast } = useToastMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exportMode, setExportMode] = useState<EXPORT_MODE | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const [approvalDetailsModal, setApprovalDetailsModal] = useAtom(approvalDetailsModalState);
  const [tableRef, setTableRef] = useState<any>(null);
  const [openExportRequestId, setOpenExportRequestId] = useState(0);
  // The fetched snapshot stays in state after the file is written, so without remembering which
  // request was already turned into a workbook any later re-render would download it again.
  const lastWrittenOpenExport = useRef(0);

  const { data: openTimesheets, isFetching: isFetchingOpenTimesheets } =
    useOpenTimesheetsToExport(openExportRequestId);

  useEffect(() => {
    if (
      openExportRequestId > lastWrittenOpenExport.current &&
      !isFetchingOpenTimesheets &&
      openTimesheets
    ) {
      lastWrittenOpenExport.current = openExportRequestId;
      generateActivityLogsTemplate(openTimesheets, 'Open_Timesheets_Activity_Logs');
    }
  }, [openExportRequestId, isFetchingOpenTimesheets, openTimesheets]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportMenuItems = [
    EXPORT_MODE.TIMESHEETS,
    EXPORT_MODE.ACTIVITY_LOGS,
    EXPORT_MODE.OPEN_TIMESHEETS,
  ];
  const open = Boolean(anchorEl);

  const onExportModalOpen = useCallback(() => {
    if (!tableRef) return;

    const selectedIds = Object.keys(tableRef.getState().rowSelection);
    if (!selectedIds.length) {
      pushWarningToast({
        title: 'Select timesheets to export',
        message:
          'Click on the timesheets in Timesheets Overview table to export selected timesheets.',
      });
    } else {
      setExportModalOpen(true);
      searchParams.set('ids', selectedIds.join(','));
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, tableRef, pushWarningToast]);

  const onExportModalClose = useCallback(() => {
    setExportModalOpen(false);
    searchParams.delete('ids');
    setSearchParams(searchParams);
    setExportMode(null);
  }, [searchParams, setSearchParams]);

  // No row selection and no period to pick: this export always covers every open timesheet, so the
  // click only has to ask for a fresh snapshot and the effect above writes the file once it lands.
  const onExportOpenTimesheets = useCallback(() => {
    setOpenExportRequestId((requestId) => requestId + 1);
  }, []);

  const onApprovalDetailsModalClose = useCallback(() => {
    setApprovalDetailsModal(null);
  }, [setApprovalDetailsModal]);

  const actionComponent = (
    <>
      <Button
        variant='outlined'
        startIcon={<DownLoadIcon />}
        style={{
          backgroundColor: colors.surfaceBackground.bg1,
          marginBottom: isSmallScreen ? '10px' : '0px',
        }}
        onClick={handleClick}
        disabled={isFetchingOpenTimesheets}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {exportMenuItems.map((mode) => (
          <MenuItem
            key={mode}
            onClick={() => {
              if (mode === EXPORT_MODE.OPEN_TIMESHEETS) {
                onExportOpenTimesheets();
              } else {
                setExportMode(mode);
                onExportModalOpen();
              }
              handleClose();
            }}
          >
            <Typography
              variant='body'
              color={colors.text.main}
              style={{ marginLeft: '4px', marginBottom: '-2px' }}
            >
              {mode}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  return (
    <>
      <PageHeader
        title='Timesheet Statuses'
        description='Review and act on timesheet status by month'
        breadcrumbs={[{ title: 'Timesheet Statuses' }]}
        actionComponent={actionComponent}
      />
      <ContentWrapper isSmallScreen={isSmallScreen}>
        <AccontantTimesheetStatusesTable onTableReady={setTableRef} />
      </ContentWrapper>
      {exportModalOpen && (
        <ExportMultiTimesheetModal
          isOpen={exportModalOpen}
          onClose={onExportModalClose}
          mode={exportMode}
        />
      )}
      {approvalDetailsModal && (
        <ApprovalDetailsModal
          approvalDetailsOpen={!!approvalDetailsModal}
          toggleApprovalDetails={onApprovalDetailsModalClose}
        />
      )}
    </>
  );
}
